// §4.18 — IMPORT CUSTOMERS, /sales/customers/import and
// /sales/customers/import/$importId (Module 09, P3-E2 PR E2-2c, D-325
// ruling 11; spec 02 §4 S7, §7).
//
// ONE FLOW, TWO HOMES. The page is module 02's import flow on the `customers`
// kind, inside the sales area, so a sales manager imports without the
// onboarding wizard. It renders the wizard's own components, not a copy of
// them: the upload panel and the jobs table of S3/S7
// (onboarding/screens/imports/upload-panel.tsx, jobs-table.tsx) and S4's job
// page (onboarding/screens/import-detail.tsx's `ImportJobDetail`), which
// carry the mapping editor, the validation summary, the row grid with every
// row's messages, commit with "load the good rows only", the commit's
// progress and its result. What this file adds is the frame: the sales
// breadcrumbs, the lane locked to `customers`, and exits that stay in sales
// (an upload opens its job here, Back lists the customer imports, and the
// committed job's action returns to the book). Onboarding's S7 Customers tab
// runs the same components on the same kind (spec 02 §4).
//
// WHO. A holder of `onboarding.import.customers` (spec 02 §7), read off the
// onboarding mirror because the code is module 02's. A reader without it
// reads the forbidden page, which states the refusal without its code. The
// server narrows the rest (spec 02 §7, D-325 ruling 6): a sales importer
// lists and opens only the customers jobs it created, and another user's job
// answers 404 `IMPORT_JOB_NOT_FOUND`, which the job page states as "Import
// not found" with the request id, the answer another tenant's job gets.
// While the role feed is in flight the page holds a skeleton; a feed that
// failed is not read as a refusal (the server still gates every call, and
// the upload panel stays closed because the mirror is fail-closed).
//
// STATES (§4.18's list, each owned where it is drawn):
//   * the jobs list: a skeleton while it loads, "No imports yet" before the
//     first, a failed read with its request id and Try again (jobs-table.tsx);
//   * a failed job: the job page's failed stage (import-detail.tsx);
//   * offline: Upload is disabled with one line saying why (upload-panel.tsx);
//   * a validate or commit refused 422 `IMPORT_IMPORTER_NOT_PERMITTED`: inline,
//     "the person who uploaded it can no longer add customers"
//     (import-problem-notice.tsx); a commit whose worker found the creator
//     demoted shows the same reason on each row (the row message
//     `import_importer_not_permitted`);
//   * a failed read of a job's rows: the row grid's error card with its
//     request id and Try again (row-grid.tsx);
//   * the error report: `error_report_url`, which for a customers job is the
//     API path `/v1/imports/{id}/error-report` (D-332 ruling 2), served on this
//     origin with the session cookie, so the job page's plain download link
//     reaches it and the workbook leaves out every column the reader is not
//     shown.
//
// PHONE. The steps stack. The mapping editor's rows collapse to one column at
// 768 px and below (imports.css), so the mapping never scrolls the page
// sideways, and the jobs table and row grid swap to their card lists.
//
// AFTER A COMMIT the book's lists are invalidated, so "Back to customers"
// opens a book that already lists what the file loaded.

import { useEffect, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { HelpTip } from "../../../../components/help-tip";
import { CardSkeleton, EmptyState, ForbiddenState } from "../../../../components/states";
import { useRoleCodesFeed } from "../../../../lib/role-codes";
import { useImport } from "../../../onboarding/hooks/onboarding-queries";
import { useOnboardingPermissions } from "../../../onboarding/hooks/permissions";
import { ImportJobDetail } from "../../../onboarding/screens/import-detail";
import { type ImportKind } from "../../../onboarding/screens/imports/import-model";
import { ImportJobsTable } from "../../../onboarding/screens/imports/jobs-table";
import { ImportUploadPanel } from "../../../onboarding/screens/imports/upload-panel";
import { salesKeys } from "../hooks/sales-queries";
import "../../../onboarding/screens/imports/imports.css";
import { guideHref } from "../../../../lib/guide-links";

/** The one lane this page runs. */
const CUSTOMERS: ImportKind = "customers";
const CUSTOMER_KINDS: readonly ImportKind[] = [CUSTOMERS];

/** Where the reader stands with the lane: the role feed in flight, refused,
 * or allowed (a failed feed reads as allowed, and the mirror stays closed). */
export type ImportGate = "pending" | "refused" | "open";

export function importGate(feed: {
  readonly isResolved: boolean;
  readonly isFeedError: boolean;
  readonly mayImport: boolean;
}): ImportGate {
  if (!feed.isResolved) return "pending";
  if (!feed.isFeedError && !feed.mayImport) return "refused";
  return "open";
}

function useImportGate(): { gate: ImportGate; canImport: (kind: ImportKind) => boolean } {
  const perms = useOnboardingPermissions();
  const feed = useRoleCodesFeed();
  return {
    gate: importGate({
      isResolved: perms.isResolved,
      isFeedError: feed.isFeedError,
      mayImport: perms.canImport(CUSTOMERS),
    }),
    canImport: (kind: ImportKind) => kind === CUSTOMERS && perms.canImport(kind),
  };
}

/** `/sales/customers/import`: upload, and the reader's customers jobs. */
export function SalesCustomersImportScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { gate, canImport } = useImportGate();

  return (
    <section className="sales-customers-import" data-testid="sales-customers-import">
      <PageHeader
        title={t("sales.customerImport.title")}
        crumbs={[{ label: t("sales.c360.crumbRoot"), to: "/sales/customers" }]}
        actions={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.customerImport.title") })}
            data-testid="sales-customers-import-intro"
            guide={{
              href: guideHref(i18n.language, "sales/add-customer"),
              label: t("nav.helpRead"),
            }}
          >
            {t("sales.customerImport.intro")}
          </HelpTip>
        }
        data-testid="sales-customers-import-head"
      />
      <ImportGateBody gate={gate}>
        <div className="catalogue-import">
          <ImportUploadPanel
            kinds={CUSTOMER_KINDS}
            canImport={canImport}
            onUploaded={(jobId) => {
              void navigate({
                to: "/sales/customers/import/$importId",
                params: { importId: jobId },
              });
            }}
          />
          <ImportJobsTable
            kinds={CUSTOMER_KINDS}
            lockedKind={CUSTOMERS}
            canRun={canImport}
            onOpen={(jobId) => {
              void navigate({
                to: "/sales/customers/import/$importId",
                params: { importId: jobId },
              });
            }}
          />
        </div>
      </ImportGateBody>
    </section>
  );
}

/** `/sales/customers/import/$importId`: one customers job, start to finish. */
export function SalesCustomerImportJobScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = useParams({ strict: false });
  const importId = typeof params.importId === "string" ? params.importId : "";
  const { gate } = useImportGate();

  // The same cache entry the job page polls, read here for the kind check and
  // the book's refresh. A refused reader asks for nothing.
  const job = useImport(gate === "open" ? importId : "");
  const status = job.data?.status;
  const kind = job.data?.kind;

  // The book lists what the file loaded the moment the reader goes back.
  useEffect(() => {
    if (status === "committed" && kind === CUSTOMERS) {
      void qc.invalidateQueries({ queryKey: salesKeys.customerLists() });
    }
  }, [status, kind, qc]);

  return (
    <section className="sales-customers-import" data-testid="sales-customers-import-job">
      <PageHeader
        // The job's own header names the file; the crumb names the page, so
        // the file name is on screen once.
        title={t("sales.customerImport.jobTitle")}
        crumbs={[
          { label: t("sales.c360.crumbRoot"), to: "/sales/customers" },
          { label: t("sales.customerImport.title"), to: "/sales/customers/import" },
        ]}
        data-testid="sales-customers-import-head"
      />
      <ImportGateBody gate={gate}>
        {kind !== undefined && kind !== CUSTOMERS ? (
          // A job of another lane reached through this address (a pasted id)
          // is not this page's to run; its own page is the wizard's S4.
          <EmptyState
            title={t("onboarding.importShared.problems.notFoundTitle")}
            body={t("sales.customerImport.otherKind")}
          />
        ) : (
          <ImportJobDetail
            id={importId}
            exits={{
              back: (
                <Link
                  to="/sales/customers/import"
                  className="wizard-header-link import-back"
                  data-testid="sales-customers-import-all"
                >
                  {t("sales.customerImport.allImports")}
                </Link>
              ),
              doneLabel: t("sales.customerImport.back"),
              onDone: () => {
                void navigate({ to: "/sales/customers" });
              },
            }}
          />
        )}
      </ImportGateBody>
    </section>
  );
}

/** The gate's three faces around the page's body. */
function ImportGateBody({ gate, children }: { gate: ImportGate; children: ReactNode }) {
  const { t } = useTranslation();
  if (gate === "pending") {
    return <CardSkeleton lines={5} label={t("sales.customerImport.loading")} />;
  }
  if (gate === "refused") {
    return (
      <ForbiddenState title={t("sales.access.title")} body={t("sales.access.importCustomers")} />
    );
  }
  return <>{children}</>;
}
